import { useCallback, useEffect, useState } from 'react'
import {
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Eye,
  Building2,
  Users,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { getApiErrorKey } from '../../utils/apiError'
import { formatDate } from '../../utils/helpers'

interface RegistrationRequest {
  id: number
  request_type: 'supplier' | 'client'
  company_name: string
  siren: string
  lastname: string
  firstname: string
  email: string
  phone: string
  erp_type: string
  comment: string
  supplier_name?: string
  supplier_siren?: string
  supplier_erp_id?: number | null
  status: 'pending' | 'approved' | 'rejected'
  processed_by_email?: string
  processed_by_firstname?: string
  processed_by_lastname?: string
  processed_at?: string
  created_at: string
}

export default function AdminRegistrationRequests() {
  const { t } = useTranslation()
  const [requests, setRequests] = useState<RegistrationRequest[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterType, setFilterType] = useState('')
  const [selectedRequest, setSelectedRequest] = useState<RegistrationRequest | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const limit = 25

  const loadRequests = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = { page, limit }
      if (filterStatus) params.status = filterStatus
      if (filterType) params.request_type = filterType
      const result = await api.getAdminRegistrationRequests(params)
      setRequests(result.data)
      setTotal(result.total)
    } catch {
      setRequests([])
    } finally {
      setLoading(false)
    }
  }, [page, filterStatus, filterType, limit])

  useEffect(() => {
    loadRequests()
  }, [loadRequests])

  const handleApprove = async (request: RegistrationRequest) => {
    if (!confirm(t('admin.registrations.approveConfirm'))) return
    setActionLoading(true)
    try {
      await api.approveRegistrationRequest(request.id)
      loadRequests()
    } catch (err: unknown) {
      alert(t(getApiErrorKey(err, 'common.error')))
    } finally {
      setActionLoading(false)
    }
  }

  const openRejectModal = (request: RegistrationRequest) => {
    setSelectedRequest(request)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const handleReject = async () => {
    if (!selectedRequest) return
    setActionLoading(true)
    try {
      await api.rejectRegistrationRequest(selectedRequest.id, rejectReason || undefined)
      setShowRejectModal(false)
      setSelectedRequest(null)
      loadRequests()
    } catch (err: unknown) {
      alert(t(getApiErrorKey(err, 'common.error')))
    } finally {
      setActionLoading(false)
    }
  }

  const totalPages = Math.ceil(total / limit)

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3" /> {t('admin.registrations.statusPending')}
          </span>
        )
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" /> {t('admin.registrations.statusApproved')}
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            <XCircle className="w-3 h-3" /> {t('admin.registrations.statusRejected')}
          </span>
        )
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        )
    }
  }

  const erpTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      dolibarr: 'Dolibarr',
      salesforce: 'Salesforce',
      business_central: 'Business Central',
      odoo: 'Odoo',
      autre: t('common.other'),
    }
    return labels[type] || type || t('common.none')
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.registrations.title')}</h1>
        <span className="text-sm text-gray-500">
          {total} demande{total > 1 ? 's' : ''}
        </span>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">{t('common.status')} :</label>
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value)
              setPage(1)
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">{t('common.all')}</option>
            <option value="pending">{t('admin.registrations.statusPending')}</option>
            <option value="approved">{t('admin.registrations.statusApproved')}</option>
            <option value="rejected">{t('admin.registrations.statusRejected')}</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">
            {t('admin.registrations.colType')} :
          </label>
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value)
              setPage(1)
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="">{t('common.all')}</option>
            <option value="supplier">{t('admin.registrations.typeSupplier')}</option>
            <option value="client">{t('admin.registrations.typeClient')}</option>
          </select>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('admin.registrations.none')}</div>
        ) : (
          <>
            {/* Mobile card layout */}
            <ul className="divide-y divide-gray-100 sm:hidden">
              {requests.map((req) => (
                <li key={req.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm">
                        {req.company_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {req.firstname} {req.lastname}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{req.email}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {req.request_type === 'client' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-accent-100 text-accent-800">
                          <Users className="w-3 h-3" />
                          {t('admin.registrations.typeClient')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          <Building2 className="w-3 h-3" />
                          {t('admin.registrations.typeSupplier')}
                        </span>
                      )}
                      {statusBadge(req.status)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-xs text-gray-400">{formatDate(req.created_at)}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setSelectedRequest(req)}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Voir le détail"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {req.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(req)}
                            disabled={actionLoading}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                            title={t('admin.registrations.approve')}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openRejectModal(req)}
                            disabled={actionLoading}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title={t('admin.registrations.reject')}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      {t('admin.registrations.colDate')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      {t('admin.registrations.colType')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      {t('admin.registrations.colCompany')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      {t('admin.registrations.colContact')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      {t('admin.registrations.colErp')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      {t('admin.registrations.colStatus')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      {t('admin.registrations.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {requests.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(req.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        {req.request_type === 'client' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-800">
                            <Users className="w-3 h-3" />
                            {t('admin.registrations.typeClient')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            <Building2 className="w-3 h-3" />
                            {t('admin.registrations.typeSupplier')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{req.company_name}</div>
                        <div className="text-xs text-gray-500">
                          SIREN: {req.siren || t('common.none')}
                        </div>
                        {req.request_type === 'client' && req.supplier_name && (
                          <div className="text-xs text-blue-600 mt-0.5">
                            ↳ {req.supplier_name}
                            {req.supplier_siren ? ` (${req.supplier_siren})` : ''}
                            {req.supplier_erp_id ? ' ✓' : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {req.firstname} {req.lastname}
                        </div>
                        <div className="text-xs text-gray-500">{req.email}</div>
                        {req.phone && <div className="text-xs text-gray-500">{req.phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {req.request_type === 'supplier'
                          ? erpTypeLabel(req.erp_type)
                          : t('common.none')}
                      </td>
                      <td className="px-4 py-3">{statusBadge(req.status)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setSelectedRequest(req)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir le détail"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {req.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(req)}
                                disabled={actionLoading}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                                title={t('admin.registrations.approve')}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openRejectModal(req)}
                                disabled={actionLoading}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                title={t('admin.registrations.reject')}
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Page {page} sur {totalPages} ({total} résultat{total > 1 ? 's' : ''})
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal détail */}
      {selectedRequest && !showRejectModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedRequest(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Détail de la demande</h3>
            </div>
            <div className="p-6 space-y-3">
              {/* Supplier info for client requests */}
              {selectedRequest.request_type === 'client' && selectedRequest.supplier_name && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="text-xs font-semibold text-blue-700 uppercase">
                    {t('admin.registrations.supplierInfo')}
                  </label>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {selectedRequest.supplier_name}
                  </p>
                  {selectedRequest.supplier_siren && (
                    <p className="text-xs text-gray-500">SIREN: {selectedRequest.supplier_siren}</p>
                  )}
                  {selectedRequest.supplier_erp_id && (
                    <p className="text-xs text-green-600">
                      ✓ Fournisseur référencé (ID: {selectedRequest.supplier_erp_id})
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('admin.registrations.colCompany')}
                  </label>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedRequest.company_name}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('common.siren')}
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.siren || t('common.none')}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('common.name')}
                  </label>
                  <p className="text-sm text-gray-900">{selectedRequest.lastname}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Prénom</label>
                  <p className="text-sm text-gray-900">{selectedRequest.firstname}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('common.email')}
                  </label>
                  <p className="text-sm text-gray-900">{selectedRequest.email}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('common.phone')}
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.phone || t('common.none')}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('common.type')} ERP
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.request_type === 'supplier'
                      ? erpTypeLabel(selectedRequest.erp_type)
                      : t('common.none')}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('admin.registrations.colStatus')}
                  </label>
                  <div className="mt-0.5">{statusBadge(selectedRequest.status)}</div>
                </div>
              </div>
              {selectedRequest.comment && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('common.comment')}
                  </label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {selectedRequest.comment}
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('admin.registrations.colDate')}
                </label>
                <p className="text-sm text-gray-900">{formatDate(selectedRequest.created_at)}</p>
              </div>
              {selectedRequest.processed_at && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Traitée le</label>
                  <p className="text-sm text-gray-900">
                    {formatDate(selectedRequest.processed_at)} par{' '}
                    {selectedRequest.processed_by_firstname} {selectedRequest.processed_by_lastname}
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              {selectedRequest.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleApprove(selectedRequest)}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                  >
                    {t('admin.registrations.approve')}
                  </button>
                  <button
                    onClick={() => {
                      setShowRejectModal(true)
                    }}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                  >
                    {t('admin.registrations.reject')}
                  </button>
                </>
              )}
              <button
                onClick={() => setSelectedRequest(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal rejet */}
      {showRejectModal && selectedRequest && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('admin.registrations.rejectConfirm')}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {selectedRequest.firstname} {selectedRequest.lastname} (
                {selectedRequest.company_name})
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.registrations.rejectReason')}
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Indiquez la raison du rejet..."
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none"
              />
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
              >
                {actionLoading ? t('common.sending') : t('admin.registrations.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
